import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import DocFormFields from "../components/DocFormFields";
import DocumentPrintCard from "../components/DocumentPrintCard";
import { DocTypeAPI, DocumentAPI } from "../lib/api";
import { useCenturion } from "../context/CenturionContext";
import { toast } from "sonner";
import {
  FileText, Lock, ArrowRight, ArrowLeft, Loader2, CheckCircle2,
  AlertTriangle, Wand2, ShieldAlert, ChevronRight,
} from "lucide-react";

const CATEGORIES = [
  { key: "", label: "All" },
  { key: "legal", label: "Legal" },
  { key: "people", label: "People" },
  { key: "financial", label: "Financial" },
  { key: "sales", label: "Sales" },
];

const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 180000; // generous client-side ceiling; backend already times out its own polling

const DocumentStudioPage = () => {
  const { unlocked } = useCenturion();
  const navigate = useNavigate();

  const [types, setTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [category, setCategory] = useState("");
  const [selected, setSelected] = useState(null); // full doc-type detail
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [counterpartyName, setCounterpartyName] = useState("");
  const [counterpartyCompany, setCounterpartyCompany] = useState("");

  const [drafting, setDrafting] = useState(false);
  const [doc, setDoc] = useState(null); // created document, polled while pending
  const [issuing, setIssuing] = useState(false);

  useEffect(() => {
    DocTypeAPI.list()
      .then(setTypes)
      .catch(() => toast.error("Failed to load document types"))
      .finally(() => setLoadingTypes(false));
  }, []);

  const filteredTypes = useMemo(
    () => (category ? types.filter((t) => t.category === category) : types),
    [types, category]
  );

  const pickType = async (t) => {
    if (t.status !== "available") return;
    try {
      const detail = await DocTypeAPI.get(t.slug);
      setSelected(detail);
      setFormData({});
      setErrors({});
      setCounterpartyName("");
      setCounterpartyCompany("");
      setDoc(null);
    } catch {
      toast.error("Failed to load document type");
    }
  };

  const reset = () => {
    setSelected(null);
    setDoc(null);
    setFormData({});
    setErrors({});
  };

  const pollDocument = async (id) => {
    const deadline = Date.now() + POLL_TIMEOUT_MS;
    while (Date.now() < deadline) {
      const polled = await DocumentAPI.get(id);
      setDoc(polled);
      if (polled.generation?.state !== "pending") return polled;
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
    }
    return null;
  };

  const handleDraft = async () => {
    if (!unlocked) { toast.error("Issuer mode locked. Type 'centurion' to unlock."); return; }
    // Client-side required-field check mirrors the backend's validate_form
    const missing = {};
    for (const field of selected.fields) {
      if (field.required && !String(formData[field.key] ?? "").trim()) {
        missing[field.key] = `${field.label} is required`;
      }
    }
    if (Object.keys(missing).length) {
      setErrors(missing);
      toast.error("Fill in the required fields");
      return;
    }

    setDrafting(true);
    setErrors({});
    try {
      const created = await DocumentAPI.create({
        doc_type: selected.slug,
        form_data: formData,
        counterparty: (counterpartyName || counterpartyCompany)
          ? { name: counterpartyName, company: counterpartyCompany }
          : null,
      });
      setDoc(created);
      toast.success("Drafting started…");
      const finalDoc = await pollDocument(created.id);
      if (finalDoc) {
        if (finalDoc.generation?.state === "fallback" && finalDoc.generation?.warning) {
          toast.warning("Manus was unavailable — used the standard template instead.");
        } else if (finalDoc.generation?.state === "ready") {
          toast.success("Draft ready");
        } else if (finalDoc.generation?.state === "failed") {
          toast.error(finalDoc.generation?.error || "Drafting failed");
        }
      }
    } catch (e) {
      console.error(e);
      const detail = e?.response?.data?.detail;
      if (detail?.errors) setErrors(detail.errors);
      toast.error(typeof detail === "string" ? detail : "Failed to start drafting");
    } finally {
      setDrafting(false);
    }
  };

  const handleIssue = async () => {
    if (!doc) return;
    setIssuing(true);
    try {
      await DocumentAPI.update(doc.id, { status: "final" });
      toast.success("Document issued!");
      navigate(`/documents/${doc.id}`);
    } catch {
      toast.error("Issue failed");
    } finally {
      setIssuing(false);
    }
  };

  const verificationUrl = doc ? `${window.location.origin}/verify-doc/${doc.verification_id}` : "";

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="mb-8">
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-[#d4a54b] mb-3">
            <Wand2 size={14} /> Document Studio
          </div>
          <h1 className="font-display text-[38px] md:text-[52px] leading-[1.05] tracking-tight">
            Every document a founder needs.<br />
            <span className="text-[#d4a54b] italic">Drafted, not typed.</span>
          </h1>
          <p className="text-white/55 mt-4 max-w-2xl text-[14px] leading-[1.7]">
            NDAs, offer letters, employment agreements, term sheets, and more — filled from a short
            form, drafted by AYI_all, always with a deterministic fallback if the model is unavailable.
          </p>
        </div>

        {!unlocked && (
          <div className="glass p-4 mb-6 flex items-center gap-4">
            <Lock size={18} className="text-[#d4a54b]" />
            <div className="flex-1 text-[13px]">
              <span className="text-white/80 font-medium">Issuer mode is locked.</span>{" "}
              <span className="text-white/55">Type the keyword anywhere to draft and issue documents.</span>
            </div>
          </div>
        )}

        {!selected ? (
          <TypePicker
            types={filteredTypes}
            loading={loadingTypes}
            category={category}
            setCategory={setCategory}
            onPick={pickType}
          />
        ) : (
          <div className="grid lg:grid-cols-[1fr_1.1fr] gap-6">
            <section className="space-y-6">
              <button onClick={reset} className="text-[13px] text-white/60 hover:text-white flex items-center gap-1.5">
                <ArrowLeft size={14} /> Choose a different document type
              </button>

              <div className="glass p-6">
                <div className="flex items-center gap-2 mb-1">
                  <FileText size={16} className="text-[#d4a54b]" />
                  <h2 className="text-[18px] font-semibold">{selected.name}</h2>
                </div>
                <p className="text-white/50 text-[13px] mb-5">{selected.summary}</p>

                {selected.requires_legal_disclaimer && (
                  <div className="mb-5 px-4 py-3 bg-[rgba(248,113,113,0.06)] border border-[rgba(248,113,113,0.25)] rounded-md flex gap-3">
                    <ShieldAlert size={16} className="text-red-400 shrink-0 mt-0.5" />
                    <p className="text-[12px] text-white/70 leading-[1.6]">
                      This document is drafted with AI assistance and is not legal advice. Have it
                      reviewed by qualified counsel before signing or relying on it.
                    </p>
                  </div>
                )}

                {(selected.fields.some((f) => f.group === "Counterparty") || true) && (
                  <div className="mb-5">
                    <h4 className="text-[10px] uppercase tracking-[0.16em] text-white/45 mb-3">Counterparty (optional)</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        className="field"
                        placeholder="Name"
                        value={counterpartyName}
                        onChange={(e) => setCounterpartyName(e.target.value)}
                      />
                      <input
                        className="field"
                        placeholder="Company"
                        value={counterpartyCompany}
                        onChange={(e) => setCounterpartyCompany(e.target.value)}
                      />
                    </div>
                  </div>
                )}

                <DocFormFields
                  fields={selected.fields}
                  values={formData}
                  onChange={setFormData}
                  errors={errors}
                />

                <button
                  onClick={handleDraft}
                  disabled={!unlocked || drafting}
                  className="btn-primary w-full justify-center mt-6 disabled:opacity-50"
                  title={!unlocked ? "Unlock issuer mode (type 'centurion')" : ""}
                >
                  {!unlocked ? <Lock size={14} /> : drafting ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
                  {drafting ? "Drafting…" : "Draft with Manus"} <ArrowRight size={14} />
                </button>
              </div>
            </section>

            <section>
              {doc ? (
                <div className="space-y-4">
                  <GenerationStatus doc={doc} />
                  <div className="max-h-[70vh] overflow-y-auto rounded-lg">
                    <DocumentPrintCard document={doc} verificationUrl={verificationUrl} />
                  </div>
                  {doc.generation?.state !== "pending" && (
                    <button
                      onClick={handleIssue}
                      disabled={issuing}
                      className="btn-primary w-full justify-center"
                    >
                      <CheckCircle2 size={14} /> {issuing ? "Issuing…" : "Issue Document"}
                    </button>
                  )}
                </div>
              ) : (
                <div className="glass p-10 text-center text-white/40 text-[13px]">
                  Fill in the form and draft to see a preview here.
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </Layout>
  );
};

const GenerationStatus = ({ doc }) => {
  const state = doc.generation?.state;
  if (state === "pending") {
    return (
      <div className="glass p-4 flex items-center gap-3">
        <Loader2 size={16} className="animate-spin text-[#d4a54b]" />
        <span className="text-[13px] text-white/70">Drafting with Manus — this can take up to a couple minutes…</span>
      </div>
    );
  }
  if (state === "fallback") {
    return (
      <div className="glass p-4 flex items-center gap-3 border-[rgba(248,163,75,0.3)]">
        <AlertTriangle size={16} className="text-amber-400" />
        <span className="text-[13px] text-white/70">
          Manus was unavailable — this draft uses the standard deterministic template.
        </span>
      </div>
    );
  }
  if (state === "failed") {
    return (
      <div className="glass p-4 flex items-center gap-3 border-[rgba(248,113,113,0.3)]">
        <AlertTriangle size={16} className="text-red-400" />
        <span className="text-[13px] text-white/70">{doc.generation?.error || "Drafting failed."}</span>
      </div>
    );
  }
  return (
    <div className="glass p-4 flex items-center gap-3 border-[rgba(34,197,94,0.3)]">
      <CheckCircle2 size={16} className="text-[#22c55e]" />
      <span className="text-[13px] text-white/70">Drafted by Manus. Review, then issue.</span>
    </div>
  );
};

const TypePicker = ({ types, loading, category, setCategory, onPick }) => (
  <div>
    <div className="flex items-center gap-1 bg-white/4 border border-white/8 rounded-md p-1 mb-6 overflow-x-auto w-fit">
      {CATEGORIES.map((c) => (
        <button
          key={c.key}
          onClick={() => setCategory(c.key)}
          className={`px-3 py-1.5 text-[12px] rounded transition-colors whitespace-nowrap ${category === c.key ? "bg-[#d4a54b] text-black font-semibold" : "text-white/60 hover:text-white"}`}
        >
          {c.label}
        </button>
      ))}
    </div>

    {loading ? (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="glass h-[110px] animate-pulse opacity-60" />)}
      </div>
    ) : (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {types.map((t) => (
          <button
            key={t.slug}
            onClick={() => onPick(t)}
            disabled={t.status !== "available"}
            className={`glass text-left p-5 transition-colors ${t.status === "available" ? "interactive hover:border-[rgba(212,165,75,0.4)] cursor-pointer" : "opacity-45 cursor-not-allowed"}`}
            title={t.status !== "available" ? t.deferred_reason : ""}
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <h3 className="text-[15px] font-semibold leading-snug">{t.name}</h3>
              {t.status === "available" ? (
                <ChevronRight size={16} className="text-[#d4a54b] shrink-0" />
              ) : (
                <span className="text-[9px] uppercase tracking-[0.1em] text-white/40 border border-white/15 rounded px-1.5 py-0.5 shrink-0">
                  {t.status === "planned" ? "Coming soon" : "Not a document"}
                </span>
              )}
            </div>
            <p className="text-[12px] text-white/50 leading-[1.5]">{t.summary}</p>
            {t.status !== "available" && t.deferred_reason && (
              <p className="text-[11px] text-white/35 mt-2 italic leading-[1.5]">{t.deferred_reason}</p>
            )}
          </button>
        ))}
      </div>
    )}
  </div>
);

export default DocumentStudioPage;
